import { useCallback, useEffect, useRef, useState } from 'react';
import { Bounds, nudgeFields as nudgeLib } from './PdfFormEnhancement';
import { message } from 'antd';

export type Position = { x0: number; y0: number; x1: number; y1: number };
export type FieldLike = { position?: Position } & Record<string, any>;
export type Handle = 'n'|'e'|'s'|'w'|'ne'|'nw'|'se'|'sw';

export type InteractionsOptions = {
  axisLockWithCtrl?: boolean;
  aspectLockWithShift?: boolean; // reserved for future use
};

export type InteractionsAPI<T extends FieldLike> = {
  dragging: null | { indices: number[]; startMouse: { x: number; y: number }; startPositions: Map<number, Position>; axis: 'x'|'y'|'both' };
  resizing: null | { idx: number; handle: Handle; startMouse: { x: number; y: number }; start: Position; axis: 'x'|'y'|'both' };
  drawing: null | { startPos: { x: number; y: number }; currentPos: { x: number; y: number } };
  startDrag: (idx: number, e: MouseEvent | React.MouseEvent) => void;
  startResize: (idx: number, handle: Handle, e: MouseEvent | React.MouseEvent) => void;
  startDrawing: (e: MouseEvent | React.MouseEvent, getContainerPoint: (e: MouseEvent | React.MouseEvent) => { x: number; y: number }) => void;
  updateDrawing: (e: MouseEvent | React.MouseEvent, getContainerPoint: (e: MouseEvent | React.MouseEvent) => { x: number; y: number }) => void;
  finishDrawing: (e: MouseEvent | React.MouseEvent, getContainerPoint: (e: MouseEvent | React.MouseEvent) => { x: number; y: number }, createFieldCallback: (rect: { x0: number; y0: number; x1: number; y1: number }) => void) => void;
  cancel: () => void;
  mouseHandlers: {
    onMouseDown: (e: React.MouseEvent<HTMLDivElement>) => void;
    onMouseMove: (e: React.MouseEvent<HTMLDivElement>) => void;
    onMouseUp: (e: React.MouseEvent<HTMLDivElement>) => void;
    onMouseLeave: (e: React.MouseEvent<HTMLDivElement>) => void;
  };
  // Copy-paste functionality
  copiedFields: FieldLike[];
  copySelectedFields: () => void;
  pasteFields: () => void;
  deleteSelectedFields: () => void;
};

function dominantAxis(dx: number, dy: number): 'x'|'y' {
  return Math.abs(dx) >= Math.abs(dy) ? 'x' : 'y';
}

export function usePdfFormInteractions<T extends FieldLike>(params: {
  fields: T[];
  setFields: (updater: (prev: T[]) => T[]) => void;
  selectedIdxs: Set<number>;
  setSelectedIdxs: (idxs: Set<number>) => void;
  bounds: Bounds;
  options?: InteractionsOptions;
  isDrawingMode?: boolean;
  getContainerPoint?: (e: MouseEvent | React.MouseEvent) => { x: number; y: number };
  createFieldCallback?: (rect: { x0: number; y0: number; x1: number; y1: number }) => void;
  cursorPos?: { x: number; y: number } | null;
  /** Called once when a drag or resize starts, before live position updates. */
  beginGesture?: () => void;
  /** Called when the pointer is released. No-op moves should not become history entries. */
  endGesture?: () => void;
  /** Discrete edits (delete, paste). Falls back to setFields. */
  commitFields?: (updater: (prev: T[]) => T[]) => void;
}): InteractionsAPI<T> {
  const {
    fields,
    setFields,
    selectedIdxs,
    setSelectedIdxs,
    bounds,
    options,
    isDrawingMode = false,
    getContainerPoint,
    createFieldCallback,
    cursorPos,
    beginGesture,
    endGesture,
    commitFields,
  } = params;
  const axisLockWithCtrl = options?.axisLockWithCtrl ?? true;

  const [dragging, setDragging] = useState<InteractionsAPI<T>['dragging']>(null);
  const [resizing, setResizing] = useState<InteractionsAPI<T>['resizing']>(null);
  const [drawing, setDrawing] = useState<InteractionsAPI<T>['drawing']>(null);

  // Copy-paste state
  const [copiedFields, setCopiedFields] = useState<T[]>([]);
  const [pasteOffset, setPasteOffset] = useState({ x: 20, y: 20 });

  const modifiersRef = useRef({ ctrl: false, shift: false });
  const latestMouseRef = useRef({ x: 0, y: 0 });

  const onDocKeyDown = useCallback((ev: KeyboardEvent) => {
    if (ev.key === 'Control') modifiersRef.current.ctrl = true;
    if (ev.key === 'Shift') modifiersRef.current.shift = true;
  }, []);
  const onDocKeyUp = useCallback((ev: KeyboardEvent) => {
    if (ev.key === 'Control') modifiersRef.current.ctrl = false;
    if (ev.key === 'Shift') modifiersRef.current.shift = false;
  }, []);

  const onDocMouseMove = useCallback((ev: MouseEvent) => {
    latestMouseRef.current = { x: ev.clientX, y: ev.clientY };
    if (dragging) {
      const { startMouse, startPositions, indices } = dragging;
      const dx = ev.clientX - startMouse.x;
      const dy = ev.clientY - startMouse.y;
      let axis: 'x'|'y'|'both' = dragging.axis;
      if (axisLockWithCtrl && modifiersRef.current.ctrl) {
        axis = dominantAxis(dx, dy);
      }
      const mx = axis === 'y' ? 0 : dx;
      const my = axis === 'x' ? 0 : dy;
      setFields(prev => {
        // Apply move relative to original startPositions to avoid compounding
        const base = prev.slice();
        const next = base.map((f, i) => base[i]);
        const temp: T[] = [...prev];
        // Start from snapshot to ensure consistent deltas
        startPositions.forEach((pos, i) => {
          temp[i] = { ...(temp[i] as any), position: { ...pos } } as T;
        });
        return nudgeLib(temp, indices, mx, my, bounds);
      });
      return;
    }
    if (resizing) {
      const { startMouse, start, handle, idx } = resizing;
      const f = fields[idx];
      if (!f || !f.position) return;
      let dx = ev.clientX - startMouse.x;
      let dy = ev.clientY - startMouse.y;
      let { x0, y0, x1, y1 } = start;
      // Axis lock when Ctrl: restrict movement to dominant axis by zeroing the perpendicular delta
      if (axisLockWithCtrl && modifiersRef.current.ctrl) {
        const ax = dominantAxis(dx, dy);
        if (ax === 'x') {
          dy = 0;
        } else {
          dx = 0;
        }
      }
      // Apply deltas by handle
      if (handle.includes('w')) x0 = x0 + dx;
      if (handle.includes('e')) x1 = x1 + dx;
      if (handle.includes('n')) y0 = y0 + dy;
      if (handle.includes('s')) y1 = y1 + dy;
      // Maintain minimum size
      if (x1 <= x0) x1 = x0 + 1;
      if (y1 <= y0) y1 = y0 + 1;
      // Clamp to bounds
      x0 = Math.min(Math.max(0, x0), Math.max(0, bounds.maxW - 1));
      y0 = Math.min(Math.max(0, y0), Math.max(0, bounds.maxH - 1));
      x1 = Math.min(Math.max(1, x1), bounds.maxW);
      y1 = Math.min(Math.max(1, y1), bounds.maxH);
      setFields(prev => {
        const next = [...prev];
        const cur = next[idx];
        if (!cur) return prev;
        next[idx] = { ...cur, position: { ...(cur.position as any), x0, y0, x1, y1 } } as T;
        return next;
      });
    }
  }, [dragging, resizing, bounds, fields, axisLockWithCtrl, setFields]);

  const endAll = useCallback(() => {
    endGesture?.();
    setDragging(null);
    setResizing(null);
    document.body.style.userSelect = '';
  }, [endGesture]);

  const onDocMouseUp = useCallback(() => {
    endAll();
  }, [endAll]);

  useEffect(() => {
    const move = (e: MouseEvent) => onDocMouseMove(e);
    const up = () => onDocMouseUp();
    const kd = (e: KeyboardEvent) => onDocKeyDown(e);
    const ku = (e: KeyboardEvent) => onDocKeyUp(e);
    const active = dragging || resizing;
    if (active) {
      window.addEventListener('mousemove', move, true);
      window.addEventListener('mouseup', up, true);
      window.addEventListener('keydown', kd);
      window.addEventListener('keyup', ku);
    }
    return () => {
      if (active) {
        window.removeEventListener('mousemove', move, true);
        window.removeEventListener('mouseup', up, true);
        window.removeEventListener('keydown', kd);
        window.removeEventListener('keyup', ku);
      }
    };
  }, [dragging, resizing, onDocMouseMove, onDocMouseUp, onDocKeyDown, onDocKeyUp]);


  // Function to delete selected fields
  const deleteSelectedFields = useCallback(() => {
    if (selectedIdxs.size === 0) return;
    
    const write = commitFields ?? setFields;
    write(prev => {
      return prev.filter((_, index) => !selectedIdxs.has(index));
    });
    
    // Clear selection after deletion
    setSelectedIdxs(new Set());
  }, [selectedIdxs, setFields, setSelectedIdxs, commitFields]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Delete') {
        deleteSelectedFields();
      }
    };
    
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [deleteSelectedFields]);

  const startDrag = useCallback((idx: number, e: MouseEvent | React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation?.();
    beginGesture?.();
    const indices = (selectedIdxs.size > 1 && selectedIdxs.has(idx)) ? Array.from(selectedIdxs) : [idx];
    const startPositions = new Map<number, Position>();
    indices.forEach(i => {
      const fp = fields[i]?.position;
      if (fp) startPositions.set(i, { ...fp });
    });
    setDragging({ indices, startMouse: { x: (e as MouseEvent).clientX, y: (e as MouseEvent).clientY }, startPositions, axis: 'both' });
    document.body.style.userSelect = 'none';
  }, [fields, selectedIdxs, beginGesture]);

  const startResize = useCallback((idx: number, handle: Handle, e: MouseEvent | React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation?.();
    const fp = fields[idx]?.position;
    if (!fp) return;
    beginGesture?.();
    setResizing({ idx, handle, startMouse: { x: (e as MouseEvent).clientX, y: (e as MouseEvent).clientY }, start: { ...fp }, axis: 'both' });
    document.body.style.userSelect = 'none';
  }, [fields, beginGesture]);

  const drawingRef = useRef(drawing);
  drawingRef.current = drawing;
  const getContainerPointRef = useRef(getContainerPoint);
  getContainerPointRef.current = getContainerPoint;
  const createFieldCallbackRef = useRef(createFieldCallback);
  createFieldCallbackRef.current = createFieldCallback;

  const cancel = useCallback(() => endAll(), [endAll]);

  // Drawing functions
  const startDrawing = useCallback((e: MouseEvent | React.MouseEvent, getContainerPoint: (e: MouseEvent | React.MouseEvent) => { x: number; y: number }) => {
    e.preventDefault();
    const pos = getContainerPoint(e);
    const next = { startPos: pos, currentPos: pos };
    drawingRef.current = next;
    setDrawing(next);
    document.body.style.userSelect = 'none';
  }, []);

  const updateDrawing = useCallback((e: MouseEvent | React.MouseEvent, getContainerPoint: (e: MouseEvent | React.MouseEvent) => { x: number; y: number }) => {
    if (!drawingRef.current) return;
    e.preventDefault();
    const pos = getContainerPoint(e);
    setDrawing(prev => prev ? { ...prev, currentPos: pos } : null);
  }, []);

  const finishDrawing = useCallback((e: MouseEvent | React.MouseEvent, getContainerPoint: (e: MouseEvent | React.MouseEvent) => { x: number; y: number }, createFieldCallback: (rect: { x0: number; y0: number; x1: number; y1: number }) => void) => {
    const current = drawingRef.current;
    if (!current) return;
    e.preventDefault();

    const raw = {
      x0: current.startPos.x,
      y0: current.startPos.y,
      x1: current.currentPos.x,
      y1: current.currentPos.y
    };
    const nx0 = Math.min(raw.x0, raw.x1);
    const ny0 = Math.min(raw.y0, raw.y1);
    const nx1 = Math.max(raw.x0, raw.x1);
    const ny1 = Math.max(raw.y0, raw.y1);
    const width = nx1 - nx0;
    const height = ny1 - ny0;

    drawingRef.current = null;
    setDrawing(null);
    document.body.style.userSelect = '';

    if (width >= 5 && height >= 5) {
      createFieldCallback({ x0: nx0, y0: ny0, x1: nx1, y1: ny1 });
    }
  }, []);

  // Keep the marquee alive while scrolling or leaving the canvas; commit only on mouseup.
  useEffect(() => {
    if (!isDrawingMode || !drawing) return;

    const onMove = (e: MouseEvent) => {
      const getPoint = getContainerPointRef.current;
      if (!getPoint || !drawingRef.current) return;
      const margin = 56;
      const maxStep = 28;
      let dy = 0;
      if (e.clientY < margin) dy = -Math.min(maxStep, margin - e.clientY);
      else if (e.clientY > window.innerHeight - margin) dy = Math.min(maxStep, e.clientY - (window.innerHeight - margin));
      if (dy !== 0) {
        const root = document.scrollingElement || document.documentElement;
        root.scrollBy(0, dy);
      }
      const pos = getPoint(e);
      drawingRef.current = { ...drawingRef.current, currentPos: pos };
      setDrawing(prev => prev ? { ...prev, currentPos: pos } : null);
    };

    const onUp = (e: MouseEvent) => {
      const getPoint = getContainerPointRef.current;
      const cb = createFieldCallbackRef.current;
      if (getPoint && cb) {
        finishDrawing(e, getPoint, cb);
      } else {
        drawingRef.current = null;
        setDrawing(null);
        document.body.style.userSelect = '';
      }
    };

    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
  }, [isDrawingMode, drawing != null, finishDrawing]);

  // Mouse handlers for drawing mode
  const mouseHandlers = {
    onMouseDown: useCallback((e: React.MouseEvent<HTMLDivElement>) => {
      if (isDrawingMode && getContainerPoint) {
        startDrawing(e, getContainerPoint);
      }
    }, [isDrawingMode, getContainerPoint, startDrawing]),

    onMouseMove: useCallback((e: React.MouseEvent<HTMLDivElement>) => {
      if (isDrawingMode && getContainerPoint) {
        updateDrawing(e, getContainerPoint);
      }
    }, [isDrawingMode, getContainerPoint, updateDrawing]),

    onMouseUp: useCallback((e: React.MouseEvent<HTMLDivElement>) => {
      if (isDrawingMode && getContainerPoint && createFieldCallback) {
        finishDrawing(e, getContainerPoint, createFieldCallback);
      }
    }, [isDrawingMode, getContainerPoint, createFieldCallback, finishDrawing]),

    onMouseLeave: useCallback((_e: React.MouseEvent<HTMLDivElement>) => {
      // Do not finish drawing/clipping when the pointer leaves the canvas
      // (scrollbars, auto-scroll, and dragging past the viewport).
    }, []),
  };

  // Copy-paste functions
  const copySelectedFields = useCallback(() => {
    const selectedFields = Array.from(selectedIdxs).map(idx => fields[idx]).filter(Boolean);
    if (selectedFields.length > 0) {
      setCopiedFields(selectedFields);
      message.success(`Copied ${selectedFields.length} field${selectedFields.length > 1 ? 's' : ''}`);
    }
  }, [selectedIdxs, fields]);

  const pasteFields = useCallback(() => {
    if (copiedFields.length === 0 || !cursorPos) return;

    const newFields = copiedFields.map((field, index) => {
      const offsetX = pasteOffset.x * (index + 1);
      const offsetY = pasteOffset.y * (index + 1);

      return {
        ...field,
        name: `${field.name}_copy${index + 1}`,
        position: field.position ? {
          ...field.position,
          x0: Math.max(0, cursorPos.x + offsetX),
          y0: Math.max(0, cursorPos.y + offsetY),
          x1: Math.max(0, cursorPos.x + offsetX + (field.position.x1 - field.position.x0)),
          y1: Math.max(0, cursorPos.y + offsetY + (field.position.y1 - field.position.y0)),
        } : undefined
      };
    });

    const write = commitFields ?? setFields;
    write(prev => [...prev, ...newFields]);
    setPasteOffset(prev => ({ x: prev.x + 10, y: prev.y + 10 })); // Increment offset for next paste
    message.success(`Pasted ${newFields.length} field${newFields.length > 1 ? 's' : ''}`);
  }, [copiedFields, cursorPos, pasteOffset, setFields, commitFields]);

  return { 
    dragging, 
    resizing, 
    drawing, 
    startDrag, 
    startResize, 
    startDrawing, 
    updateDrawing, 
    finishDrawing, 
    cancel, 
    mouseHandlers, 
    copiedFields, 
    copySelectedFields, 
    pasteFields, 
    deleteSelectedFields 
  };
}
